import { useEffect, useState } from "react";

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import {
  getSmtpSettings,
  testSmtpSettings,
  updateSmtpSettings,
  type SmtpSecurity,
} from "@/api/platformSettings";
import { PageHeader } from "@/components/PageHeader";
import { useAuthStore } from "@/store/authStore";

/** OVH's documented outbound relay. Applied by the "preset" button. */
const OVH_PRESET = {
  smtp_host: "ssl0.ovh.net",
  smtp_port: 587,
  smtp_security: "STARTTLS" as SmtpSecurity,
};

const SECURITIES: SmtpSecurity[] = ["STARTTLS", "SSL", "NONE"];

export function SmtpSettingsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const me = useAuthStore((s) => s.me);
  const isGod = Boolean(me?.is_god);

  const settings = useQuery({
    queryKey: ["platform-settings", "smtp"],
    queryFn: getSmtpSettings,
    enabled: isGod,
  });

  const [enabled, setEnabled] = useState(false);
  const [host, setHost] = useState("");
  const [port, setPort] = useState(587);
  const [security, setSecurity] = useState<SmtpSecurity>("STARTTLS");
  const [username, setUsername] = useState("");
  const [from, setFrom] = useState("");
  // Empty means "leave the stored password alone" — it is never sent back to us.
  const [password, setPassword] = useState("");
  const [clearPassword, setClearPassword] = useState(false);
  const [testTo, setTestTo] = useState("");

  useEffect(() => {
    const d = settings.data;
    if (!d) return;
    setEnabled(d.smtp_enabled);
    setHost(d.smtp_host ?? "");
    setPort(d.smtp_port);
    setSecurity(d.smtp_security);
    setUsername(d.smtp_username ?? "");
    setFrom(d.smtp_from ?? "");
    setPassword("");
    setClearPassword(false);
  }, [settings.data]);

  const save = useMutation({
    mutationFn: () =>
      updateSmtpSettings({
        smtp_enabled: enabled,
        smtp_host: host || null,
        smtp_port: port,
        smtp_security: security,
        smtp_username: username || null,
        smtp_from: from || null,
        ...(clearPassword
          ? { smtp_password: "" }
          : password
            ? { smtp_password: password }
            : {}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["platform-settings", "smtp"] });
    },
  });

  const test = useMutation({
    mutationFn: () => testSmtpSettings(testTo),
  });

  if (!isGod) {
    return (
      <Box>
        <PageHeader title={t("smtp.title")} subtitle={t("smtp.subtitle")} helpTerm="smtp" />
        <Alert severity="warning">{t("smtp.forbidden")}</Alert>
      </Box>
    );
  }

  const passwordSet = settings.data?.smtp_password_set ?? false;
  const saveError = save.error as { response?: { data?: { detail?: string } } } | null;

  return (
    <Box>
      <PageHeader title={t("smtp.title")} subtitle={t("smtp.subtitle")} />

      <Card sx={{ maxWidth: 720 }}>
        <CardContent>
          <Stack spacing={2.5}>
            <FormControlLabel
              control={
                <Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
              }
              label={t("smtp.enabled")}
            />
            {!enabled && (
              <Alert severity="info">{t("smtp.disabledNotice")}</Alert>
            )}

            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setHost(OVH_PRESET.smtp_host);
                  setPort(OVH_PRESET.smtp_port);
                  setSecurity(OVH_PRESET.smtp_security);
                }}
              >
                {t("smtp.applyOvhPreset")}
              </Button>
              <Typography variant="caption" color="text.secondary">
                {OVH_PRESET.smtp_host}:{OVH_PRESET.smtp_port} · STARTTLS
              </Typography>
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label={t("smtp.host")}
                value={host}
                onChange={(e) => setHost(e.target.value)}
                fullWidth
                required={enabled}
              />
              <TextField
                label={t("smtp.port")}
                type="number"
                value={port}
                onChange={(e) => setPort(Number(e.target.value))}
                sx={{ width: { sm: 140 } }}
              />
              <TextField
                label={t("smtp.security")}
                select
                value={security}
                onChange={(e) => setSecurity(e.target.value as SmtpSecurity)}
                sx={{ width: { sm: 180 } }}
              >
                {SECURITIES.map((s) => (
                  <MenuItem key={s} value={s}>
                    {t(`smtp.securityOption.${s}`)}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>

            <TextField
              label={t("smtp.username")}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              fullWidth
              autoComplete="off"
            />

            <Stack spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  label={t("smtp.password")}
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={passwordSet ? t("smtp.passwordKept") : ""}
                  disabled={clearPassword}
                  fullWidth
                  autoComplete="new-password"
                />
                {passwordSet && (
                  <Chip size="small" color="success" label={t("smtp.passwordSet")} />
                )}
              </Stack>
              {passwordSet && (
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={clearPassword}
                      onChange={(e) => {
                        setClearPassword(e.target.checked);
                        if (e.target.checked) setPassword("");
                      }}
                    />
                  }
                  label={t("smtp.clearPassword")}
                />
              )}
              <Typography variant="caption" color="text.secondary">
                {t("smtp.passwordStorageNote")}
              </Typography>
            </Stack>

            <TextField
              label={t("smtp.from")}
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              fullWidth
              placeholder="CORTEX <no-reply@exemple.fr>"
            />

            {save.isError && (
              <Alert severity="error">
                {t(`smtp.err.${saveError?.response?.data?.detail ?? "error"}`, {
                  defaultValue: saveError?.response?.data?.detail ?? t("smtp.err.error"),
                })}
              </Alert>
            )}
            {save.isSuccess && <Alert severity="success">{t("smtp.saved")}</Alert>}

            <Box>
              <Button
                variant="contained"
                onClick={() => save.mutate()}
                disabled={save.isPending || (enabled && !host)}
              >
                {t("smtp.save")}
              </Button>
            </Box>

            <Divider />

            <Stack spacing={1.5}>
              <Typography variant="subtitle2">{t("smtp.testTitle")}</Typography>
              <Typography variant="caption" color="text.secondary">
                {t("smtp.testNote")}
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <TextField
                  label={t("smtp.testTo")}
                  type="email"
                  value={testTo}
                  onChange={(e) => setTestTo(e.target.value)}
                  fullWidth
                />
                <Button
                  variant="outlined"
                  onClick={() => test.mutate()}
                  disabled={test.isPending || !testTo || !settings.data?.smtp_host}
                >
                  {t("smtp.sendTest")}
                </Button>
              </Stack>
              {test.data?.ok && <Alert severity="success">{t("smtp.testOk")}</Alert>}
              {test.data && !test.data.ok && (
                <Alert severity="error">
                  {t("smtp.testFailed")} — {test.data.detail}
                </Alert>
              )}
              {test.isError && <Alert severity="error">{t("smtp.testFailed")}</Alert>}
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
